import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ENTITY_TYPE_LABELS, RELATION_KIND_LABELS, type EntityType } from '@raro/domain';
import { cx } from '@raro/ui';
import { Link } from 'react-router';
import { api } from '../lib/api';
import type { Relation, ResolvedEntity } from '../lib/types';

export function entityHref(e: ResolvedEntity | null) {
  if (!e) return undefined;
  if (e.type === 'source') return `/fontes/${e.id}`;
  if (e.type === 'evidence') return `/fontes/${e.meta.sourceId as string}`;
  if (e.type === 'hypothesis') return `/hipoteses/${e.id}`;
  return undefined;
}

export function EntityChip({ e }: { e: ResolvedEntity | null }) {
  if (!e) return <span className="rr-faint">(removido)</span>;
  const href = entityHref(e);
  const body = (
    <>
      <span className="rr-rel__type">{ENTITY_TYPE_LABELS[e.type]}</span>
      {e.type === 'evidence' ? <span className="rr-serif rr-serif--italic">“{e.title}{e.text.length > 80 ? '…' : ''}”</span> : e.title}
    </>
  );
  return href ? <Link to={href} style={{ textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: 'var(--rr-border-strong)' }}>{body}</Link> : <span>{body}</span>;
}

/** Relações vistas a partir de um objeto: "sustenta → hipótese", "← contradita por evidência". */
export function RelationList({ relations, self, canEdit }: { relations: Relation[]; self: { type: EntityType; id: string }; canEdit: boolean }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/relations/${id}`),
    onSuccess: () => qc.invalidateQueries(),
  });
  if (relations.length === 0) return <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>sem relações ainda.</p>;
  return (
    <div>
      {relations.map((r) => {
        const outgoing = r.fromType === self.type && r.fromId === self.id;
        const other = outgoing ? r.to : r.from;
        const label = outgoing ? RELATION_KIND_LABELS[r.kind] : `${passive(r.kind)}`;
        return (
          <div key={r.id} className="rr-rel">
            <span className={cx('rr-rel__kind', `rr-rel__kind--${r.kind}`)}>{label}</span>
            <div>
              <EntityChip e={other} />
              {r.note && <div className="rr-rel__note">{r.note}</div>}
            </div>
            {canEdit && (
              <button type="button" className="rr-close" style={{ width: 24, height: 24 }} aria-label="Remover relação" onClick={() => remove.mutate(r.id)}>
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function passive(kind: Relation['kind']) {
  return (
    {
      supports: 'sustentada por',
      contradicts: 'contradita por',
      derived_from: 'origem de',
      related_to: 'relaciona-se com',
      answers: 'respondida por',
      tests: 'testada por',
      caused_by: 'causa de',
      raises: 'levantada por',
      implements: 'implementada por',
      measures: 'medida por',
    } as const
  )[kind];
}
